import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ImageSlider } from './Slider';
import { ChevronDown, X } from 'lucide-react';

const Projects = ({ scrollTo, experienceRef }: any) => {
  type Project = {
    id: number;
    title: string;
    images: string[];
    date: string;
    description: string;
    tags: string[];
    link: string;
  };

  const [clickedId, setClickedId] = useState<number | null>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const projects = [
    {
      id: 1,
      title: "Showcase & Booking Website - L'Aura Sacrée",
      images: [
        './asset/wordpress/1.png',
        './asset/wordpress/2.png',
        './asset/wordpress/3.png',
        './asset/wordpress/4.png'
      ],
      date: '2025',
      description:
        'Developed a WordPress showcase and booking website for an energy therapist. Clean and soothing design, highlighting her services while allowing clients to easily book appointments online.',
      tags: ['WordPress', 'Elementor', 'SEO'],
      link: 'https://l-aura-sacree.fr',
    },   
    {
      id: 2,
      title: 'Wikicampers Mobile App',
      images: [
        './asset/wikicampers-app/1.png',
        './asset/wikicampers-app/2.png',
        './asset/wikicampers-app/3.png'
      ],
      date: '2024',
      description:
        'Developed the entire frontend of the Wikicampers mobile application using React with Ionic Capacitor. Worked in close collaboration with a backend IT to ensure seamless integration and deliver a smooth, user-friendly experience.',
      tags: ['React', 'Ionic Capacitor', 'Mobile Development'],
      link: 'https://www.wikicampers.com',
    },
     
    {
      id: 3,
      title: 'Wikicampers Platform',
      images: [
        './asset/wikicampers/1.png',
        './asset/wikicampers/2.png',
        './asset/wikicampers/3.png'
      ],
      date: 'since 2023',
      description:
        'Contributed to the evolution and bug resolution of the Wikicampers platform built with Symfony. Later took on the responsibility of ticket management and assignment when necessary, improving SLA performance and ensuring smoother workflow across the IT team.',
      tags: ['Symfony', 'Bug Fixing', 'Ticket Management', 'Jira'],
      link: 'https://www.wikicampers.com',
    },    
    {
      id: 4,
      title: 'Internal company PWA (Wikigreen)',
      images: [
        './asset/wikigreen/1.png',
        './asset/wikigreen/2.jpeg',
        './asset/wikigreen/3.png',
        './asset/wikigreen/4.png',
      ],
      date: '2023',
      description:
        'Wikigreen is a Progressive Web App (PWA) designed to encourage collective eco-friendly actions. Users earn points by completing environmentally positive tasks, such as recycling, using public transport, or participating in clean-up events. These actions are tracked and verified collectively, promoting community engagement. With the points collected, users can unlock internal rewards, like badges, achievements, or access to exclusive content. Wikigreen makes sustainability fun, social, and rewarding.',
      tags: ['Next.js', 'Tailwind', 'Directus'],
      link: '',
    },
    {
      id: 5,
      title: "Photographer WordPress Portfolio - Maurice Rebeix",
      images: [
        './asset/maurice/1.png',
        './asset/maurice/2.png',
        './asset/maurice/3.png'
      ],
      date: '2022',
      description:
        "A WordPress portfolio website created for photographer Maurice Rebeix. The site highlights his photographic work, with a design inspired by his ideas and artistic vision. I worked to faithfully represent his style and allow his work to be showcased in the best way possible.",
      tags: ['WordPress', 'SEO'],
      link: 'https://mauricerebeix.com/',
    },
    {
      id: 6,
      title: 'Personal Portfolio Website',
      images: null,
      date: 'Continuously evolving',
      description:
      'My personal showcase website built with React and Tailwind. It highlights my projects, skills, and experience, serving as a central hub to present my work and make it accessible to potential clients and employers. This project also allows me to express my creativity freely, unlike client or company projects where I am not always the decision maker.',
      tags: ['React.js', 'Tailwind', 'Framer Motion'],
      link: 'https://florianganderatz.fr',
    },
    
  ];

  return (
    <section className="md:snap-start h-auto relative md:h-screen py-20 px-6 md:px-20  text-gray-900 dark:text-white">
      <div className="max-w-6xl mx-auto">
        <motion.h2
          className="text-5xl md:text-6xl ralewaytitle mb-12 !italic mt-16 md:mt-0 text-center md:text-left"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Projects
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-4 gap-6">
          {projects.map((project) => (
            <motion.div
              key={project.id}
              layoutId={`card-${project.id}`}
              className="aspect-square bg-white dark:bg-gray-900 flex flex-col justify-between rounded-xl p-4 cursor-pointer shadow hover:shadow-lg transition"
              onClick={() => {
                setClickedId(project.id);
                setTimeout(() => setSelectedProject(project), 200);
              }}
            >
              <motion.div
                initial={{ opacity: 1 }}
                animate={{ opacity: clickedId === project.id ? 0 : 1 }}
                transition={{ duration: 0.2 }}
                className='h-full'
              >
                <div className='flex flex-col justify-between h-full'>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">{project.title}</h3>
                    <p
                      className="text-sm text-gray-700 dark:text-gray-300 line-clamp-4"
                      dangerouslySetInnerHTML={{ __html: project.description }}
                    />
                  </div>
                  <div className='flex justify-between flex-col'>
                    <div className='relative h-14'>
                      {project.images && project.images.map((img, i) => (
                        <motion.img
                        key={i}
                        src={img}
                        className="w-14 aspect-square object-cover rounded-xl absolute shadow-lg"
                        style={{
                          rotate: -15 + i * 15,
                          translateX: i * 20,
                        }}
                        whileHover={{ scale: 1.1, zIndex: 10 }}
                      />
                      ))}
                    </div>
                    <p className="text-sm text-right font-semibold text-gray-700 dark:text-gray-300 line-clamp-4">
                      {project.date}
                    </p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {selectedProject && (
          <>
            <motion.div
              className="fixed inset-0 z-40 bg-black/50 dark:bg-gray-900/50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.400 }}
              onClick={() => {
                setSelectedProject(null);
                setTimeout(() => setClickedId(null), 200);
              }}
            />
            <motion.div
              layoutId={`card-${selectedProject.id}`}
              className="fixed flex flex-col justify-between inset-4 md:inset-20 z-50 bg-white dark:bg-gray-900 rounded-xl p-6 shadow-2xl overflow-auto"
              initial={{ opacity: 1, scale: 1 }}
              animate={{ scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.2 }}
              >
                <button
                  onClick={() => {
                    setSelectedProject(null);
                    setTimeout(() => setClickedId(null), 200);
                  }}
                  className="absolute top-4 right-4 z-50 text-gray-600 dark:text-gray-300 hover:text-red-500"
                >
                  <X size={24} />
                </button>
                {selectedProject.images && selectedProject.images.length > 0 ? (
                  <ImageSlider images={selectedProject.images} />
                ) : (
                  <div className="w-full h-48 md:h-64 flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg">
                    <span className="text-gray-500 dark:text-gray-400 text-sm italic">
                      No preview available
                    </span>
                  </div>
                )}
                <h2 className="text-2xl font-bold mt-4 mb-2">{selectedProject.title}</h2>
                <p
                  className="text-gray-700 dark:text-gray-300 mb-4"
                  dangerouslySetInnerHTML={{ __html: selectedProject.description }}
                />
                <div className="flex flex-wrap gap-2">
                  {selectedProject.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-sm bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100 px-2 py-1 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <div
        onClick={() => {if(!selectedProject)scrollTo(experienceRef)}}
        className={selectedProject ? 
          `absolute hidden bottom-8 inset-x-0 md:flex justify-center animate-bounce cursor-default z-20` :  
          `absolute hidden bottom-8 inset-x-0 md:flex justify-center animate-bounce cursor-pointer z-40`}
      >
        <ChevronDown size={32} />
      </div>
    </section>
  );
};

export default Projects;
